import { useEffect, useState } from "react";
import { PRODUCT_NAME } from "@pv/branding";
import { api, ApiError, clearAuth, getOrgId, getToken, setAuth, type AuthUser } from "./api.js";
import { LocaleProvider, useLocale, useT } from "./i18n/index.js";
import Dashboard from "./Dashboard.js";
import ProjectWorkspace from "./Project.js";

type Route = { view: "auth" } | { view: "dash" } | { view: "project"; id: string; name: string };

export default function App(): JSX.Element {
  return (
    <LocaleProvider>
      <AppShell />
    </LocaleProvider>
  );
}

function AppShell(): JSX.Element {
  const [route, setRoute] = useState<Route>(getToken() && getOrgId() ? { view: "dash" } : { view: "auth" });
  const [user, setUser] = useState<AuthUser | null>(null);
  const t = useT();
  const { locale, setLocale } = useLocale();

  function logout(): void {
    void api.logout().catch(() => undefined);
    clearAuth();
    setUser(null);
    setRoute({ view: "auth" });
  }

  return (
    <>
      <header className="topbar">
        <div className="logo" onClick={() => route.view !== "auth" && setRoute({ view: "dash" })}>
          <div className="mark">☀</div>
          <b>{PRODUCT_NAME}</b>
        </div>
        <div className="crumb">
          {route.view === "project" ? (
            <>
              <span style={{ cursor: "pointer", marginRight: 10, fontSize: 16 }} onClick={() => setRoute({ view: "dash" })}>←</span>
              <b>{route.name}</b>
            </>
          ) : route.view === "dash" ? (
            t("app.nav.projects")
          ) : (
            t("app.nav.signIn")
          )}
        </div>
        <div className="ribbon">
          {user && <span className="rb">👤 {user.name}</span>}
          {route.view !== "auth" && (
            <button className="rb" onClick={logout}>{t("app.signOut")}</button>
          )}
          {/* E45: a REAL locale switcher (the previous 🌐 EN span was a dead control) */}
          <button
            className="rb"
            title={locale === "de" ? "Switch to English" : "Auf Deutsch umschalten"}
            onClick={() => setLocale(locale === "de" ? "en" : "de")}
          >
            🌐 {locale.toUpperCase()}
          </button>
        </div>
      </header>

      {route.view === "auth" && (
        <AuthScreen
          onDone={(u) => {
            setUser(u);
            setRoute({ view: "dash" });
          }}
        />
      )}
      {route.view === "dash" && (
        <Dashboard onOpenProject={(id, name) => setRoute({ view: "project", id, name })} />
      )}
      {route.view === "project" && (
        <ProjectWorkspace projectId={route.id} onBack={() => setRoute({ view: "dash" })} />
      )}
    </>
  );
}

function AuthScreen({ onDone }: { onDone: (u: AuthUser) => void }): JSX.Element {
  const t = useT();
  // E40/E49: emailed links land here — /?reset-token=… opens the reset form,
  // /?verify-token=… confirms the address, /?invite=… opens registration with
  // the invite carried through submit (the front door for the closed pilot).
  const urlResetToken = new URLSearchParams(window.location.search).get("reset-token");
  const urlVerifyToken = new URLSearchParams(window.location.search).get("verify-token");
  const urlInviteToken = new URLSearchParams(window.location.search).get("invite");
  const [mode, setMode] = useState<"login" | "register" | "forgot" | "reset">(
    urlResetToken ? "reset" : urlInviteToken ? "register" : "login",
  );
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [orgName, setOrgName] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  // E49: invite state — token kept in memory (stripped from the URL), email
  // locked when the invite is bound so a mismatch can never be discovered by failing
  const [inviteToken, setInviteToken] = useState<string | null>(urlInviteToken);
  const [inviteEmailLocked, setInviteEmailLocked] = useState(false);

  useEffect(() => {
    if (!urlVerifyToken) return;
    void api
      .verifyEmail(urlVerifyToken)
      .then(() => setInfo(t("auth.emailVerified")))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => window.history.replaceState(null, "", window.location.pathname));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!urlInviteToken) return;
    void api
      .inviteInfo(urlInviteToken)
      .then((res) => {
        if (res.state === "valid") {
          setInfo(t("auth.inviteWelcome"));
          if (res.email) {
            setEmail(res.email);
            setInviteEmailLocked(true); // the API rejects a mismatch — never let the installer discover that by failing
          }
        } else {
          // every failure state explains itself in the user's locale (E47's lesson)
          setInviteToken(null);
          setMode("login");
          setError(t(res.state === "used" ? "auth.inviteUsed" : res.state === "expired" ? "auth.inviteExpired" : "auth.inviteUnknown"));
        }
      })
      .catch(() => {
        setInviteToken(null);
        setMode("login");
        setError(t("auth.inviteUnknown"));
      })
      .finally(() => window.history.replaceState(null, "", window.location.pathname));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(): Promise<void> {
    setError("");
    setInfo("");
    setBusy(true);
    try {
      if (mode === "register") {
        const res = await api.register({ email, name, password, organizationName: orgName, ...(inviteToken ? { inviteToken } : {}) });
        setAuth(res.token, res.organization.id);
        onDone(res.user);
      } else if (mode === "forgot") {
        const res = await api.requestPasswordReset(email);
        setInfo(res.message);
      } else if (mode === "reset") {
        await api.resetPassword(urlResetToken ?? "", password);
        window.history.replaceState(null, "", window.location.pathname);
        setPassword("");
        setMode("login");
        setInfo(t("auth.passwordChanged"));
      } else {
        const res = await api.login({ email, password });
        const org = res.organizations[0];
        if (!org) throw new Error(t("auth.noOrganization"));
        setAuth(res.token, org.id);
        onDone(res.user);
      }
    } catch (e) {
      // E50: a 429 is shown as the calm LOCALIZED line (DE cross-pinned to the API constant)
      if (e instanceof ApiError && e.status === 429) setError(t("auth.tooManyAttempts"));
      else setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-wrap">
      <div className="card auth-card">
        <h1 style={{ marginBottom: 4 }}>
          {mode === "login" ? t("auth.signIn") : mode === "register" ? t("auth.createAccount") : mode === "forgot" ? t("auth.resetPassword") : t("auth.setNewPassword")}
        </h1>
        {/* E36: approved positioning statement, verbatim (claim surface — via pinned catalogue keys) */}
        <div className="muted" style={{ marginBottom: 8, fontSize: 13 }}>
          {t("claims.positioning")}
        </div>
        {/* E36: approved pilot positioning statement, verbatim */}
        <div className="muted" style={{ marginBottom: 16, fontSize: 12 }}>
          {t("claims.pilotScope")}
        </div>
        {mode === "register" && (
          <>
            <div className="field"><label>{t("auth.yourName")}</label><input value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="field"><label>{t("auth.companyName")}</label><input value={orgName} onChange={(e) => setOrgName(e.target.value)} /></div>
          </>
        )}
        {mode !== "reset" && (
          <div className="field"><label>{t("auth.email")}</label>
            <input type="email" value={email} disabled={mode === "register" && inviteEmailLocked} onChange={(e) => setEmail(e.target.value)} />
            {mode === "register" && inviteEmailLocked && <span className="muted" style={{ fontSize: 11.5 }}>{t("auth.inviteEmailLocked")}</span>}
          </div>
        )}
        {mode !== "forgot" && (
          <div className="field"><label>{mode === "reset" ? t("auth.newPassword") : t("auth.password")} {(mode === "register" || mode === "reset") && <span className="muted">{t("auth.minChars")}</span>}</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void submit()} /></div>
        )}
        {error && <div className="msg err">{error}</div>}
        {info && <div className="msg info">{info}</div>}
        <button className="btn btn-primary" style={{ width: "100%", marginTop: 6 }} disabled={busy} onClick={() => void submit()}>
          {busy ? <span className="spinner" /> : mode === "login" ? t("auth.signInAction") : mode === "register" ? t("auth.createAction") : mode === "forgot" ? t("auth.requestLink") : t("auth.savePassword")}
        </button>
        <div style={{ marginTop: 12, fontSize: 12.5, textAlign: "center" }}>
          {mode === "login" ? (
            <>
              {t("auth.noAccount")} <a href="#" onClick={(e) => { e.preventDefault(); setMode("register"); }}>{t("auth.registerCompany")}</a>
              {" · "}
              <a href="#" onClick={(e) => { e.preventDefault(); setMode("forgot"); }}>{t("auth.forgotPassword")}</a>
            </>
          ) : (
            <>{t("auth.alreadyRegistered")} <a href="#" onClick={(e) => { e.preventDefault(); setMode("login"); }}>{t("auth.signIn")}</a></>
          )}
        </div>
      </div>
    </div>
  );
}
